/**
 * Main App Component
 * Sets up routing and global providers
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import ToastContainer from './components/ToastContainer';
import { ErrorBoundary } from './components/ErrorBoundary';
import LoginScreen from './components/Auth/LoginScreen';
import RegisterScreen from './components/Auth/RegisterScreen';
import LobbyScreen from './components/Lobby/LobbyScreen';
import ProfileScreen from './components/Lobby/ProfileScreen';
import SpecialCardsScreen from './components/Lobby/SpecialCardsScreen';
import HowToPlayScreen from './components/Lobby/HowToPlayScreen';
import { GameTableScreen } from './components/GameTable';
import UnauthorizedPage from './pages/UnauthorizedPage';
import SoloTable from './pages/SoloTable';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import UsersPage from './pages/admin/UsersPage';
import TablesPage from './pages/admin/TablesPage';
import GamesPage from './pages/admin/GamesPage';
import BotsPage from './pages/admin/BotsPage';
import SettingsPage from './pages/admin/SettingsPage';

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
          {/* Default screen while lobby/matchmaking are on hold — 1 human vs
              bots on a real, server-driven table (see SoloTable.tsx). Needs
              a real login now that it plays a real hand instead of showing
              static mock data. */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <SoloTable />
              </ProtectedRoute>
            }
          />
          <Route
            path="/solo"
            element={
              <ProtectedRoute>
                <SoloTable />
              </ProtectedRoute>
            }
          />

          {/* Public Routes */}
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/register" element={<RegisterScreen />} />
          <Route path="/specials" element={<SpecialCardsScreen />} />
          <Route path="/how-to-play" element={<HowToPlayScreen />} />

          {/* Protected Routes */}
          <Route
            path="/lobby"
            element={
              <ProtectedRoute>
                <LobbyScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileScreen />
              </ProtectedRoute>
            }
          />
          <Route
            path="/game/:tableId"
            element={
              <ProtectedRoute>
                <GameTableWrapper />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="tables" element={<TablesPage />} />
            <Route path="games" element={<GamesPage />} />
            <Route path="bots" element={<BotsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>

          {/* Unauthorized */}
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Anything unrecognized also falls back to the solo table */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

          {/* Global Toast Container */}
          <ToastContainer />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

// Wrapper component to extract tableId from route params
const GameTableWrapper: React.FC = () => {
  const { tableId } = useParams<{ tableId: string }>();

  if (!tableId) {
    return <Navigate to="/lobby" replace />;
  }

  return <GameTableScreen tableId={tableId} />;
};

export default App;
